<template>
  <section ref="screenRoot" class="page board-screen" data-test="board-screen">
    <!-- Hero -->
    <div class="board-hero">
      <div class="anim" data-delay="80">
        <RouterLink
          v-if="focusProject && isMultiProject"
          :to="{ name: 'board', params: { boardId } }"
          class="board-back"
          data-test="board-back"
        >← All projects on this board</RouterLink>
        <Eyebrow pulse>Board · {{ boardType === 'kanban' ? 'Kanban' : 'Sprints' }}</Eyebrow>
        <DisplayHeading :lead="headingLead" :accent="boardType === 'kanban' ? 'kanban' : 'overview'" />
        <p class="display-sub" data-test="board-sub">
          {{ subLine }}
          <BoardTypeBadge v-if="boardType === 'kanban'" board-type="kanban" />
          <RiskPill v-if="!isShared && health.band === 'red'" variant="high">At risk</RiskPill>
          <RiskPill v-else-if="!isShared && health.band === 'amber'" variant="med">Watch</RiskPill>
          <RiskPill v-else-if="!isShared && health.band === 'green'" variant="low">On track</RiskPill>
        </p>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorObj" class="error-state anim" data-delay="180">
      <p class="error-title">Couldn't load this board</p>
      <p class="error-msg">{{ errorObj.message }}</p>
      <button class="retry" type="button" @click="store.load(boardId, true)">Retry</button>
    </div>

    <!-- Loading -->
    <div v-else-if="isInitialLoading" class="skeletons anim" data-delay="200" data-test="loading">
      <div class="skel-row big" />
      <div class="skel-row" />
    </div>

    <!-- Empty -->
    <div v-else-if="sprints.length === 0" class="empty anim" data-delay="200" data-test="board-empty">
      No scorecard data recorded for this board yet.
    </div>

    <!-- Shared board: per-project breakdown, no merged metrics -->
    <div v-else-if="isShared" class="shared anim" data-delay="220" data-test="board-shared">
      <div class="shared-explainer" data-test="board-shared-explainer">
        <span class="shared-badge">Shared board</span>
        <p class="shared-lead">
          <strong>{{ headingLead }}</strong> is a single Jira board whose sprints carry tickets from
          <strong>{{ projects.length }}</strong> different projects:
          <span class="shared-keys">{{ projects.join(" · ") }}</span>.
        </p>
        <p class="shared-why">
          This is normal when several teams plan their work on one board but tag each ticket with its
          own project key. Because every project has its own team, commitments and delivery cadence,
          the scorecard scores them <strong>separately and never blends them into one number</strong> —
          a merged figure would let a healthy project hide a struggling one.
        </p>
        <p v-if="sharedSprintExample" class="shared-example" data-test="board-shared-example">
          <span class="ex-label">On this board</span>
          the sprint <strong>{{ sharedSprintExample.name }}</strong> carries tickets from
          <span class="shared-keys">{{ sharedSprintExample.keys.join(", ") }}</span>
          — {{ sharedSprintExample.keys.length }} projects in one sprint. Each project's scorecard
          counts only its own tickets, so the numbers stay accurate. Open that sprint in Jira to see
          the mix yourself.
        </p>
        <p v-else class="shared-example" data-test="board-shared-example">
          <span class="ex-label">On this board</span>
          each project runs its own sprints; the scorecard scores every sprint under the project key
          its tickets are tagged with, and never rolls them together.
        </p>
        <p class="shared-cta">↓ Pick a project below to open its own scorecard.</p>
      </div>
      <div class="shared-grid">
        <RouterLink
          v-for="g in projectGroups"
          :key="g.key"
          :to="{ name: 'board', params: { boardId }, query: { project: g.key } }"
          class="shared-card"
          :data-test="`board-project-${g.key}`"
        >
          <span class="shared-key">{{ g.key }}</span>
          <span class="shared-meta">{{ g.count }} sprint{{ g.count === 1 ? '' : 's' }} on this board</span>
          <span v-if="g.latest" class="shared-latest">Latest: {{ g.latest.sprint_name }}</span>
        </RouterLink>
      </div>
    </div>

    <!-- Single-project Kanban board -->
    <div v-else-if="boardType === 'kanban'" class="kanban-body anim" data-delay="240" data-test="board-kanban">
      <article class="now-card">
        <span class="lbl-mono">Now · Rolling window</span>
        <h2 class="now-title">{{ focus?.sprint_name }}</h2>
        <p class="state-line">{{ rangeLine }}</p>
      </article>
      <HygieneWatch
        v-if="focusDetail"
        :project-key="projectKey"
        :sprint="focusDetail"
        scope-label="rolling window"
      />
      <JqlFilters scope="project" :project-key="projectKey" />
    </div>

    <!-- Single-project Scrum board -->
    <div v-else class="board-grid anim" data-delay="240" data-test="board-grid">
      <aside class="rail-left">
        <AlertsRail :project-key="projectKey" :alerts="alerts" />
      </aside>

      <div class="content-stack">
        <nav v-if="sprintTabs.length > 1" class="sprint-tabs" aria-label="Switch sprint">
          <RouterLink
            v-for="s in sprintTabs"
            :key="s.sprint_id"
            :to="{ name: 'sprint', params: { key: projectKey, id: s.sprint_id } }"
            :class="['sprint-tab', { 'is-active-sprint': s.sprint_state === 'active' }]"
          >
            <span class="dot" :class="ragDot(s)" aria-hidden="true" />
            <span class="name">{{ s.sprint_name }}</span>
            <span v-if="s.sprint_state === 'active'" class="state">live</span>
          </RouterLink>
        </nav>

        <article class="now-card" data-test="board-now-card">
          <header class="now-head">
            <div>
              <span class="lbl-mono">Now · Latest sprint
                <span v-if="isActive" style="color:var(--muted-2);">(in progress)</span></span>
              <h2 class="now-title">{{ focus?.sprint_name }}</h2>
              <p class="state-line">{{ stateLine }}</p>
            </div>
            <RouterLink
              v-if="focus"
              :to="{ name: 'sprint', params: { key: projectKey, id: focus.sprint_id } }"
              class="open-link"
            >Open scorecard →</RouterLink>
          </header>

          <div class="kpi-grid" :class="tileGridClass">
            <HealthTile
              v-for="tile in headlines"
              :key="tile.metricId"
              :label="tile.label"
              :current="tile.current"
              :previous="tile.previous"
              :delta="tile.delta"
              :rag="tile.rag"
              :series="tile.series"
              :format="formatFor(tile.metricId)"
              :good-direction="goodDirectionFor(tile.metricId)"
            />
          </div>

          <div v-if="topRisks.length > 0" class="watch">
            <h4>Top {{ topRisks.length }} thing{{ topRisks.length > 1 ? 's' : '' }} to watch</h4>
            <div v-for="r in topRisks" :key="`${r.metric}-${r.rule}`" class="watch-item">
              <span :class="['code', r.severity]">{{ r.metric }}</span>
              <span class="text">{{ r.message }}</span>
            </div>
          </div>

          <p v-if="narrative" class="narrative">{{ narrative }}</p>
        </article>

        <InsightsSection v-if="focusInsights" :audit="focusInsights" />
        <HygieneWatch v-if="focusDetail" :project-key="projectKey" :sprint="focusDetail" />
        <CapexTrend :sprints="sprints" />
        <Phase1b3Section v-if="sprints.length > 0" :sprints="sprints" />
        <JqlFilters scope="project" :project-key="projectKey" />
      </div>

      <SprintHistoryRail
        class="rail-right"
        :project-key="projectKey"
        :sprints="sprints"
        :current-sprint-id="focus?.sprint_id"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";
import AlertsRail from "@/components/AlertsRail.vue";
import BoardTypeBadge from "@/components/BoardTypeBadge.vue";
import CapexTrend from "@/components/CapexTrend.vue";
import HealthTile from "@/components/HealthTile.vue";
import HygieneWatch from "@/components/HygieneWatch.vue";
import InsightsSection from "@/components/InsightsSection.vue";
import JqlFilters from "@/components/JqlFilters.vue";
import Phase1b3Section from "@/features/project/components/Phase1b3Section.vue";
import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import Eyebrow from "@/components/scorecard/Eyebrow.vue";
import RiskPill from "@/components/scorecard/RiskPill.vue";
import SprintHistoryRail from "@/components/scorecard/SprintHistoryRail.vue";
import { useReveal } from "@/composables/useReveal";
import { fmtDate } from "@/lib/format";
import { sprintProgress, type RiskSeverity } from "@/lib/insights";
import { scanGovernanceAlerts } from "@/lib/governanceAlerts";
import { useBoardsStore } from "@/stores/boards";
import { useSprintDetailStore } from "@/stores/sprintDetail";
import type { RagBand, SprintListItem } from "@/api/types";

const route = useRoute();
const store = useBoardsStore();
const detailStore = useSprintDetailStore();
const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);

const boardId = computed(() => String(route.params.boardId ?? ""));
const allSprints = computed(() => store.items(boardId.value));
const boardName = computed(() => store.boardName(boardId.value));
const boardType = computed(() => store.boardType(boardId.value));
const projects = computed(() => store.projectKeys(boardId.value));

// Shared boards (sprints spanning >1 project) can be opened "focused" on a
// single project via ?project=KEY. In that mode we render the normal
// single-project scorecard, scoped to that project's sprints — instead of the
// per-project breakdown grid. This is what makes a shared-board project card
// actually reach a scorecard (otherwise /p/KEY bounced straight back here).
const focusProject = computed(() => {
  const p = String(route.query.project ?? "");
  return p && projects.value.includes(p) ? p : "";
});
const isMultiProject = computed(() => projects.value.length > 1);
const isShared = computed(() => isMultiProject.value && !focusProject.value);
const projectKey = computed(() => focusProject.value || projects.value[0] || "");
const sprints = computed(() =>
  focusProject.value
    ? allSprints.value.filter((s) => s.project_key === focusProject.value)
    : allSprints.value,
);

const errorObj = computed(() => store.error(boardId.value));
const isLoading = computed(() => store.loading(boardId.value));
const isInitialLoading = computed(() => isLoading.value && sprints.value.length === 0);

const headingLead = computed(() => boardName.value || `Board ${boardId.value}`);
const subLine = computed(() => {
  if (isShared.value) return `${projects.value.join(" · ")}`;
  const proj = projectKey.value ? `${projectKey.value} · ` : "";
  if (boardType.value === "kanban") {
    const days = focus.value?.throughput?.window_days ?? 14;
    return `${proj}Rolling ${days}-day window`;
  }
  return `${proj}${sprints.value.length} sprint${sprints.value.length === 1 ? "" : "s"}`;
});

// A REAL example for the shared-board explainer: the sprint on this board that
// genuinely spans the most project keys. Multiple projects share one sprint_id
// when their tickets co-mingle in the same sprint — that's the row a PM can open
// in Jira to verify. Null when no single sprint mixes projects (the board is
// "shared" only because separate sprints belong to separate projects), so we
// never claim co-mingling that isn't there.
const sharedSprintExample = computed<{ name: string; keys: string[] } | null>(() => {
  const byId = new Map<number, { name: string; keys: Set<string> }>();
  for (const s of allSprints.value) {
    const e = byId.get(s.sprint_id) ?? { name: s.sprint_name, keys: new Set<string>() };
    if (s.project_key) e.keys.add(s.project_key);
    byId.set(s.sprint_id, e);
  }
  let best: { name: string; keys: Set<string> } | null = null;
  for (const e of byId.values()) {
    if (e.keys.size > 1 && (best === null || e.keys.size > best.keys.size)) best = e;
  }
  return best ? { name: best.name, keys: [...best.keys].sort() } : null;
});

// Per-project groups for the shared-board breakdown (newest sprint first).
// Always derived from the full sprint set, independent of any focus filter.
const projectGroups = computed(() =>
  projects.value.map((key) => {
    const list = allSprints.value.filter((s) => s.project_key === key);
    return { key, count: list.length, latest: list[0] };
  }),
);

// Focus = active sprint if any, else the most recent (single-project only).
const activeSprint = computed(() => sprints.value.find((s) => s.sprint_state === "active"));
const focus = computed<SprintListItem | undefined>(() => activeSprint.value ?? sprints.value[0]);
const isActive = computed(() => focus.value?.sprint_state === "active");

const focusDetail = computed(() =>
  focus.value && projectKey.value
    ? detailStore.detail(projectKey.value, focus.value.sprint_id)
    : null,
);
const focusInsights = computed(() => focusDetail.value?.insights);

const insights = computed(() => store.insightsFor(sprints.value));
const headlines = computed(() => insights.value.headlines);
const topRisks = computed(() => insights.value.risks.slice(0, 3));
const narrative = computed(() => insights.value.narrative);
const health = computed(() => insights.value.health);
const alerts = computed(() => scanGovernanceAlerts(projectKey.value, sprints.value));

const tileGridClass = computed(() => (headlines.value.length === 4 ? "kpi-4" : "kpi-3"));
const sprintTabs = computed<SprintListItem[]>(() => sprints.value.slice(0, 6));

function ragDot(s: SprintListItem): RagBand {
  const m7 = s.headline_metrics?.M7?.rag;
  if (m7 && m7 !== "none") return m7;
  if (s.low_signal) return "red";
  return "none";
}

const progress = computed(() => sprintProgress(focus.value));
const stateLine = computed(() => {
  const s = focus.value;
  if (!s) return "";
  const dates = `${fmtDate(s.sprint_start)} → ${fmtDate(s.sprint_end)}`;
  if (s.sprint_state === "active" && progress.value) {
    const p = progress.value;
    const phase = p.isOverdue
      ? `overdue · day ${p.elapsedDays} of ${p.totalDays}`
      : `in progress · day ${p.elapsedDays} of ${p.totalDays}`;
    return `${phase} · ${dates}`;
  }
  const cap = s.sprint_state.charAt(0).toUpperCase() + s.sprint_state.slice(1);
  return `${cap} · ${dates}`;
});
const rangeLine = computed(() =>
  focus.value ? `${fmtDate(focus.value.sprint_start)} → ${fmtDate(focus.value.sprint_end)}` : "",
);

function formatFor(metricId: string): "pct" | "int" | "num" {
  if (metricId === "M9" || metricId === "M15") return "int";
  if (metricId === "M19") return "num";
  return "pct";
}
function goodDirectionFor(metricId: string): "higher" | "lower" {
  if (metricId === "M7" || metricId === "M8") return "higher";
  return "lower";
}
// kept for downstream compat; narrative already encodes severity
function _severityToBand(s: RiskSeverity): string { return s; }

function loadFocusDetail(): void {
  if (focus.value && projectKey.value && !isShared.value) {
    void detailStore.load(projectKey.value, focus.value.sprint_id);
  }
}

onMounted(() => {
  if (boardId.value) store.load(boardId.value);
});
watch(() => boardId.value, (b) => { if (b) store.load(b); });
watch(() => focus.value?.sprint_id, () => loadFocusDetail(), { immediate: true });
</script>

<style scoped>
.board-screen { position: relative; }
.board-hero { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 36px; }
.board-back { display: inline-block; margin-bottom: 12px; font-size: 13px; color: var(--brand); text-decoration: none; font-weight: 500; }
.board-back:hover { text-decoration: underline; }

.error-state { padding: 16px; background: var(--red-soft); border: 1px solid color-mix(in srgb, var(--red) 30%, transparent); border-radius: 12px; color: var(--red-ink); display: grid; gap: 8px; }
.error-title { font-weight: 500; margin: 0; }
.error-msg { font-size: 13px; margin: 0; color: var(--ink-2); }
.retry { align-self: start; background: var(--bg-elev); border: 1px solid color-mix(in srgb, var(--red) 30%, transparent); padding: 6px 14px; border-radius: 8px; cursor: pointer; color: var(--red-ink); }

.skeletons { display: grid; gap: 12px; }
.skel-row { height: 96px; border-radius: 12px; background: var(--skeleton); }
.skel-row.big { height: 160px; }
.empty { padding: 48px; border: 1px dashed var(--line); border-radius: 12px; color: var(--muted); text-align: center; }

.shared { display: grid; gap: 16px; }
.shared-explainer {
  display: grid; gap: 10px;
  padding: 18px 20px; border: 1px solid var(--line); border-left: 3px solid var(--brand);
  border-radius: 12px; background: var(--brand-soft);
}
.shared-badge {
  justify-self: start; font-family: "JetBrains Mono", monospace; font-size: 10px;
  letter-spacing: 0.12em; text-transform: uppercase; font-weight: 600; color: var(--brand);
  padding: 3px 8px; border-radius: 6px; background: color-mix(in srgb, var(--brand) 12%, transparent);
}
.shared-lead { font-size: 15px; color: var(--ink); margin: 0; line-height: 1.5; }
.shared-keys { font-weight: 600; color: var(--ink); }
.shared-why { font-size: 13px; color: var(--ink-2); margin: 0; line-height: 1.6; }
.shared-example { font-size: 13px; color: var(--ink-2); margin: 0; line-height: 1.7; }
.ex-label {
  font-family: "JetBrains Mono", monospace; font-size: 10px; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--muted); margin-right: 4px;
}
.shared-cta { font-size: 13px; font-weight: 500; color: var(--brand); margin: 2px 0 0; }
.shared-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
.shared-card { display: grid; gap: 4px; padding: 16px 18px; border: 1px solid var(--line); border-radius: 12px; background: var(--bg-elev); text-decoration: none; transition: border-color var(--speed-quick) var(--ease), transform var(--speed-quick) var(--ease); }
.shared-card:hover { border-color: var(--ink); transform: translateY(-1px); }
.shared-key { font-size: 18px; font-weight: 600; color: var(--ink); letter-spacing: -0.02em; }
.shared-meta { font-size: 12px; color: var(--muted); }
.shared-latest { font-size: 12px; color: var(--ink-2); }

.board-grid { display: grid; grid-template-columns: 280px minmax(0, 1fr) 260px; gap: 24px; }
@media (max-width: 1280px) { .board-grid { grid-template-columns: 280px minmax(0, 1fr); } .rail-right { display: none; } }
@media (max-width: 960px) { .board-grid { grid-template-columns: 1fr; } }
.rail-right { align-self: start; position: sticky; top: 80px; }
.content-stack { display: grid; gap: 24px; min-width: 0; }
.kanban-body { display: flex; flex-direction: column; gap: 18px; }

.now-card { background: var(--bg-elev); border: 1px solid var(--line); border-radius: 16px; padding: 28px; display: grid; gap: 22px; min-width: 0; }
.now-head { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 12px; }
.lbl-mono { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--fg-subtle, #8a8f9c); }
.now-title { font-size: 24px; font-weight: 500; letter-spacing: -0.025em; margin: 4px 0 6px; }
.state-line { font-size: 13px; color: var(--muted); margin: 0; }
.open-link { color: var(--brand); cursor: pointer; font-size: 14px; font-weight: 500; }
.open-link:hover { text-decoration: underline; }

.kpi-grid { display: grid; gap: 14px; }
.kpi-grid.kpi-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.kpi-grid.kpi-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.kpi-grid > * { min-width: 0; }
@media (max-width: 1600px) { .board-grid .kpi-grid.kpi-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 720px) { .kpi-grid.kpi-3, .kpi-grid.kpi-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

.watch h4 { font-family: "JetBrains Mono", monospace; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); font-weight: 500; margin: 0 0 12px; }
.watch-item { display: grid; grid-template-columns: 80px 1fr; gap: 12px; align-items: center; padding: 12px 0; border-top: 1px solid var(--line); }
.watch-item:first-of-type { border-top: 0; padding-top: 4px; }
.watch-item .code { font-family: "JetBrains Mono", monospace; font-size: 11px; padding: 3px 8px; border-radius: 6px; width: fit-content; }
.watch-item .code.high { background: var(--red-soft); color: var(--red-ink); }
.watch-item .code.medium { background: var(--amber-soft); color: var(--amber-ink); }
.watch-item .code.low { background: var(--green-soft); color: var(--green-ink); }
.watch-item .text { font-size: 14px; color: var(--ink-2); }

.narrative { background: var(--brand-soft); padding: 16px 18px; border-radius: 12px; font-family: "Instrument Serif", serif; font-style: italic; font-size: 18px; line-height: 1.55; color: var(--ink); margin: 0; letter-spacing: -0.01em; }

.sprint-tabs { display: flex; gap: 8px; padding: 4px 0 18px; overflow-x: auto; border-bottom: 1px solid var(--line); }
.sprint-tab { display: inline-flex; align-items: center; gap: 8px; padding: 7px 13px; border: 1px solid var(--line); border-radius: 999px; background: var(--bg-elev); font-size: 13px; color: var(--ink-2); text-decoration: none; white-space: nowrap; }
.sprint-tab:hover { border-color: var(--ink); }
.sprint-tab .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--muted-2); }
.sprint-tab .dot.red { background: var(--red); }
.sprint-tab .dot.amber { background: var(--amber); }
.sprint-tab .dot.green { background: var(--green); }
.sprint-tab .name { font-weight: 500; }
.sprint-tab .state { font-family: "JetBrains Mono", monospace; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--green); padding: 1px 6px; border-radius: 999px; background: color-mix(in srgb, var(--green) 14%, transparent); }
</style>
